import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import companiesApi, { CompanyDTO } from '../services/companies.ts';
import { ArrowLeftIcon, PencilIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const formatCurrency = (value?: number | null) => {
  const v = Number(value || 0);
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value?: number | null) => {
  const v = Number(value || 0);
  return `${v.toFixed(2)}%`;
};

const formatCNPJ = (cnpj?: string) => {
  const s = (cnpj || '').replace(/\D/g, '').padStart(14, '0');
  return s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const CompanyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading, isError } = useQuery<CompanyDTO>(
    ['company', id],
    () => companiesApi.get(Number(id)),
    { enabled: Boolean(id) }
  );

  const revenue = Number(data?.annualRevenue || 0);
  const profit = Number(data?.annualProfit || 0);
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

  const statusClass =
    data?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/companies')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Empresas
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100 text-primary-600 mr-4">
                <BuildingOfficeIcon className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{data?.name || 'Empresa'}</h1>
                {data?.cnpj && <p className="text-gray-600 mt-2">CNPJ: {formatCNPJ(data.cnpj)}</p>}
              </div>
            </div>

            {data && (
              <div className="flex items-center space-x-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}
                >
                  {data.status === 'active' ? 'Ativa' : 'Inativa'}
                </span>
                {id && (
                  <Link
                    to={`/companies/${id}/edit`}
                    className="inline-flex items-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Editar
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ) : isError ? (
          <div className="text-center text-red-500 p-8">Erro ao carregar.</div>
        ) : data ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Informações</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Nome</dt>
                  <dd className="font-medium">{data.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">CNPJ</dt>
                  <dd className="font-medium">{formatCNPJ(data.cnpj)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Status</dt>
                  <dd className="font-medium">{data.status === 'active' ? 'Ativa' : 'Inativa'}</dd>
                </div>
              </dl>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Financeiro</h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">% Lucro</dt>
                  <dd className="font-medium">{formatPercent(data.profitPercent)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Lucro Anual</dt>
                  <dd className="font-medium">{formatCurrency(data.annualProfit)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Receita Anual</dt>
                  <dd className="font-medium">{formatCurrency(data.annualRevenue)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Despesas Anuais</dt>
                  <dd className="font-medium">{formatCurrency(data.annualExpenses)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Valuation</dt>
                  <dd className="font-medium">{formatCurrency(data.valuation)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Margem</dt>
                  <dd className="font-medium">{`${margin.toFixed(2)}%`}</dd>
                </div>
              </dl>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CompanyDetail;
